import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { runCode, runCodeWithCustomInput } from "../services/assessment.api";
import { setRetakes } from "../store/CurrentQuestion/action";
import {
  applyDiagnostics,
  clearDiagnostics,
} from "../utils/programming/errorDiagnostics";

/**
 * Hook for code execution
 * @param {object} options - Configuration options
 * @param {string} options.code - Current code
 * @param {string} options.questionId - Question ID
 * @param {number} options.selectedLanguage - Selected language ID
 * @param {boolean} options.attemptsExhausted - Whether attempts are exhausted
 * @param {number} options.maxAttempts - Maximum attempts
 * @param {number} options.attemptsUsed - Number of attempts used
 * @param {string} options.candidateAssessmentId - Candidate assessment ID
 * @param {string} options.assessmentId - Screening test ID
 * @param {string} options.skillName - Skill name
 * @param {boolean} options.practiceMode - Whether in practice mode
 * @param {object} options.editorRef - Monaco editor ref
 * @param {object} options.monacoRef - Monaco namespace ref
 * @param {string} options.monacoLanguage - Monaco language ID
 * @param {function} options.setShowOutput - Function to show/hide output
 * @param {function} options.setActiveTab - Function to set active tab
 * @returns {object} Code execution state and functions
 */
export const useCodeExecution = ({
  code,
  questionId,
  selectedLanguage,
  attemptsExhausted,
  maxAttempts,
  attemptsUsed,
  candidateAssessmentId,
  assessmentId,
  skillName,
  practiceMode,
  editorRef,
  monacoRef,
  monacoLanguage,
  setShowOutput,
  setActiveTab,
  onUpdateRetakes,
}) => {
  const dispatch = useDispatch();
  const [isRunningCode, setIsRunningCode] = useState(false);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [customResult, setCustomResult] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  /**
   * Run code with custom input
   */
  const runWithCustomInput = useCallback(
    async (customInput) => {
      setCustomLoading(true);

      // Skip custom input execution in practice mode
      if (practiceMode) {
        setCustomResult([
          {
            input: customInput || "Sample input",
            output: "Practice mode - no execution",
            executionTime: "0ms",
            status: "Practice Mode",
          },
        ]);
        setCustomLoading(false);
        return;
      }

      try {
        const payload = {
          candidateAssessmentId: candidateAssessmentId,
          assessmentId: assessmentId,
          questionId: questionId,
          skill: skillName || "Programming",
          code,
          languageId: selectedLanguage,
          customInput,
        };

        const response = await runCodeWithCustomInput(assessmentId, payload);
        setCustomResult(response || null);
        // Ensure result is visible
        setShowOutput(true);
        setActiveTab(1); // Custom Input tab
      } catch (error) {
        console.error("Error running code with custom input:", error);
        setCustomResult([]);
      } finally {
        setCustomLoading(false);
      }
    },
    [
      code,
      questionId,
      selectedLanguage,
      candidateAssessmentId,
      assessmentId,
      skillName,
      practiceMode,
    ],
  );

  /**
   * Run code with test cases
   */
  const handleRunCode = useCallback(async () => {
    if (!questionId || !selectedLanguage || attemptsExhausted) return;

    // Skip code execution in practice mode
    if (practiceMode) {
      // In practice mode, show a mock result
      setResults([
        {
          testCase: "Sample Test Case",
          status: "Practice Mode",
          input: "Sample input",
          expectedOutput: "Sample expected output",
          actualOutput: "Practice mode - no execution",
          executionTime: "0ms",
        },
      ]);
      setSummary({
        totalTestCases: 1,
        passedTestCases: 1,
        failedTestCases: 0,
        totalExecutionTime: "0ms",
        message: "Practice Mode - All test cases passed",
      });
      // Open output modal in practice mode
      setShowOutput(true);
      setActiveTab(2); // Results tab
      return;
    }

    setIsRunningCode(true);
    if (editorRef.current && monacoRef.current) {
      clearDiagnostics(editorRef.current, monacoRef.current);
    }

    try {
      const payload = {
        candidateAssessmentId: candidateAssessmentId,
        assessmentId: assessmentId,
        questionId: questionId,
        skill: skillName || "Programming",
        code,
        languageId: selectedLanguage,
      };

      const response = await runCode(assessmentId, payload);

      console.log("✅ Code execution response:", {
        hasResults: !!response?.results,
        hasSummary: !!response?.summary,
        resultsCount: response?.results?.length || 0,
        summary: response?.summary,
      });

      setResults(response?.results || []);
      setSummary(response?.summary || null);

      // Check if all test cases passed and trigger celebration
      // Only show celebration if there are actual test cases and all passed
      const allPassed =
        response?.summary?.total > 0 &&
        response?.summary?.passed > 0 &&
        response?.summary?.total === response?.summary?.passed;
      if (allPassed) {
        setShowCelebration(true);
      } else {
        // Clear celebration if not all passed
        setShowCelebration(false);
      }

      // Apply error diagnostics
      const errorMessages = [];
      for (const r of response?.results || []) {
        if (
          r.status !== "Passed" &&
          typeof r.actual === "string" &&
          r.actual.length
        ) {
          errorMessages.push(r.actual);
        }
      }
      if (errorMessages.length && editorRef.current && monacoRef.current) {
        applyDiagnostics(
          editorRef.current,
          monacoRef.current,
          errorMessages,
          monacoLanguage,
        );
      }

      // Auto-open footer and switch to Results tab
      setShowOutput(true);
      setActiveTab(2); // Results tab
    } catch (error) {
      console.error("❌ Code execution error:", error);
      setResults([]);
      setSummary({
        message: `Error: ${error.response?.data?.error || error.message}`,
      });
      if (editorRef.current && monacoRef.current) {
        applyDiagnostics(
          editorRef.current,
          monacoRef.current,
          [error.response?.data?.error || error.message],
          monacoLanguage,
        );
      }
    } finally {
      if (onUpdateRetakes) {
        onUpdateRetakes(Math.min(maxAttempts, attemptsUsed + 1));
      }
      setIsRunningCode(false);
    }
  }, [
    questionId,
    selectedLanguage,
    attemptsExhausted,
    code,
    maxAttempts,
    attemptsUsed,
    practiceMode,
    candidateAssessmentId,
    assessmentId,
    skillName,
    editorRef,
    monacoRef,
    monacoLanguage,
    setShowOutput,
    setActiveTab,
    dispatch,
  ]);

  return {
    isRunningCode,
    results,
    setResults,
    summary,
    setSummary,
    customResult,
    setCustomResult,
    customLoading,
    setCustomLoading,
    showCelebration,
    setShowCelebration,
    handleRunCode,
    runWithCustomInput,
  };
};
